import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Target, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const METRICS = [
  { key: "call", label: "Calls" },
  { key: "contact", label: "Contacts" },
  { key: "appt_set_indiv", label: "Appointments Set" },
  { key: "referral_received", label: "Referrals Received" },
  { key: "networking_event", label: "Networking Events" },
  { key: "workout", label: "Workouts" },
  { key: "case_submitted", label: "Cases Submitted" },
  { key: "case_paid", label: "Cases Paid" },
];

export default function Challenges() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", metric: "call", target: 50, reward: "" });

  const load = () => api.get("/challenges").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/challenges", { ...form, target: Number(form.target) });
      toast.success("Challenge created");
      setOpen(false);
      setForm({ title: "", description: "", metric: "call", target: 50, reward: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this challenge?")) return;
    await api.delete(`/challenges/${id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Team</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            Challenges <Target weight="fill" className="text-[#fb7185]" />
          </h1>
          <p className="text-zinc-600 mt-2">Team goals. Everyone pushes. Everyone wins.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#09090b] text-white border-2 border-black hover:bg-black shadow-[4px_4px_0_0_#2563eb] font-bold uppercase tracking-widest text-xs h-11" data-testid="new-challenge-btn">
                <Plus size={16} weight="bold" className="mr-1" /> New Challenge
              </Button>
            </DialogTrigger>
            <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#09090b]">
              <DialogHeader><DialogTitle className="font-display font-black tracking-tight">New Challenge</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="font-bold uppercase text-xs tracking-widest">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" data-testid="challenge-title" />
                </div>
                <div>
                  <Label className="font-bold uppercase text-xs tracking-widest">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-2 border-black rounded-none focus-visible:ring-0" rows={2} data-testid="challenge-desc" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-bold uppercase text-xs tracking-widest">Metric</Label>
                    <select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} className="mt-1 w-full h-11 border-2 border-black px-3 bg-white font-medium">
                      {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="font-bold uppercase text-xs tracking-widest">Target</Label>
                    <Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" />
                  </div>
                </div>
                <div>
                  <Label className="font-bold uppercase text-xs tracking-widest">Reward</Label>
                  <Input value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} placeholder="$500 bonus, dinner, etc." className="mt-1 border-2 border-black rounded-none h-11 focus-visible:ring-0" />
                </div>
                <Button type="submit" className="w-full h-11 rounded-none bg-[#2563eb] text-white border-2 border-black shadow-[4px_4px_0_0_#09090b] hover:shadow-[6px_6px_0_0_#09090b] hover:-translate-y-0.5 font-bold uppercase tracking-widest" data-testid="save-challenge-btn">
                  Create
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {rows.map((c) => {
          const pct = Math.min(100, Math.round(((c.current || 0) / (c.target || 1)) * 100));
          return (
            <div key={c.id} className="brut bg-white p-5" data-testid={`challenge-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-display font-black text-2xl tracking-tight">{c.title}</div>
                  <div className="text-sm text-zinc-600 mt-1">{c.description}</div>
                </div>
                {isAdmin && (
                  <button onClick={() => del(c.id)} className="border-2 border-black p-2 hover:bg-[#fb7185]" data-testid={`delete-challenge-${c.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </div>
              <div className="mt-4 h-6 border-2 border-black relative">
                <div className="h-full bg-[#34d399]" style={{ width: `${pct}%` }} />
                <div className="absolute inset-0 flex items-center justify-center text-xs font-black">
                  {c.current} / {c.target} · {pct}%
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs uppercase tracking-widest font-bold">
                <span className="text-zinc-500">Reward</span>
                <span className="px-2 py-1 bg-[#2563eb] border-2 border-black">{c.reward || "—"}</span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="col-span-full brut bg-white p-10 text-center text-zinc-500">
            <Target size={32} className="mx-auto mb-2" />
            No team challenges yet{isAdmin ? ". Create one!" : ". Your admin will post one soon."}
          </div>
        )}
      </div>
    </div>
  );
}
